/**
 * OAuth callback route (`/auth/callback`).
 *
 * This is the auth-side entry point the T7 spec designates for handling
 * post-OAuth query parameters. React Router's `useLocation().search`
 * mirrors `window.location.search` without touching the DOM global — which
 * makes the component trivially testable under `MemoryRouter` and avoids a
 * direct CP1-sensitive reference. (If a future auth flow needs to *mutate*
 * `window.location` — e.g. to initiate a full-page OAuth redirect — this is
 * the approved place for that read/write. `packages/shared/**` forbids it
 * entirely.)
 *
 * Supports two incoming shapes so the route is forward-compatible:
 *
 *   - `?credential=<google-id-token>` — legacy parity. The legacy app used
 *     Google Identity Services (GIS) in-page, which calls an onCredential
 *     callback inline rather than navigating. If a future iteration chooses
 *     redirect-based GIS or a proxy, it can land here.
 *
 *   - `?code=<google-auth-code>` — if we later switch to the OAuth Code flow,
 *     the backend `/api/auth/google` endpoint would need to accept `code`
 *     instead of `credential`. Until then, a `?code=` callback surfaces a
 *     useful error rather than silently failing.
 *
 * On success: store the access token + cached user via `tokens.setSession`,
 * invalidate TanStack Query's `auth.me` key so a fresh `/auth/me` refetch
 * hydrates downstream hooks, then redirect to the intended destination (or
 * `/home`).
 *
 * On failure: redirect to `/login?error=<reason>`. The LoginPage surfaces
 * the reason inline.
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { keys, useApi } from '@shopista/shared/api/hooks';
import { routes } from '../routes/paths';
import type { WebTokenStorage } from './tokenStorage';

export interface OAuthCallbackProps {
  tokens: WebTokenStorage;
}

type CallbackState =
  | { phase: 'exchanging' }
  | { phase: 'redirect-success'; to: string }
  | { phase: 'redirect-error'; reason: string };

function readQueryParam(search: string, key: string): string | null {
  return new URLSearchParams(search).get(key);
}

export function OAuthCallback({ tokens }: OAuthCallbackProps): JSX.Element {
  const api = useApi();
  const qc = useQueryClient();
  const location = useLocation();
  const [state, setState] = useState<CallbackState>({ phase: 'exchanging' });

  // Guard against React 18 StrictMode double-invocation: we must NOT exchange
  // a one-shot credential twice (the backend would 400 the replay).
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // `location.search` is the router's mirror of `window.location.search`.
    // Using it keeps the file testable under MemoryRouter without reading
    // the real DOM global.
    const credential = readQueryParam(location.search, 'credential');
    const code = readQueryParam(location.search, 'code');
    const error = readQueryParam(location.search, 'error');
    const inviteToken = readQueryParam(location.search, 'invite');

    if (error) {
      setState({ phase: 'redirect-error', reason: error });
      return;
    }

    // Token-based flow (legacy + current backend contract).
    if (credential) {
      void (async () => {
        try {
          const payload =
            inviteToken !== null
              ? { credential, inviteToken }
              : { credential };
          const resp = await api.auth.loginWithGoogle(payload);
          // T49.3 — a new Google user without a consent signal gets
          // `{ consentRequired: true }`. This redirect-mode placeholder can't show
          // the in-card consent step, so route to /login where it lives; the user
          // re-runs sign-in there and gets the "Almost there" step.
          if ('consentRequired' in resp) {
            setState({ phase: 'redirect-success', to: routes.login });
            return;
          }
          tokens.setSession({
            accessToken: resp.sessionToken,
            user: resp.user,
          });
          // Tell TanStack Query to re-fetch /auth/me so downstream hooks see
          // the new user without a full reload.
          void qc.invalidateQueries({ queryKey: keys.auth.me });
          // T16 Stage 4 AC 4.3/4.4: when the backend signaled the invite
          // was rejected (mismatched email, expired, hard-deleted) we still
          // sign the user in — but we MUST NOT silently drop them into a
          // default household with no signal. Forward the message to /home
          // via `?invite_error=...` so InviteConsumer can render it.
          const to = resp.inviteError
            ? `${routes.home}?invite_error=${encodeURIComponent(resp.inviteError)}`
            : routes.home;
          setState({ phase: 'redirect-success', to });
        } catch (e) {
          const reason = e instanceof Error ? e.message : 'Login failed';
          setState({ phase: 'redirect-error', reason });
        }
      })();
      return;
    }

    if (code) {
      // OAuth Code flow placeholder — the backend does not currently accept
      // `code`. Surface a descriptive error rather than looping.
      setState({
        phase: 'redirect-error',
        reason:
          'OAuth Code flow is not wired. Use the Google Identity Services credential flow (?credential=...).',
      });
      return;
    }

    setState({
      phase: 'redirect-error',
      reason: 'Missing credential in OAuth callback',
    });
  }, [api, qc, tokens, location.search]);

  if (state.phase === 'exchanging') {
    return (
      <div data-testid="oauth-callback-loading" style={{ padding: '2rem' }}>
        Completing sign-in...
      </div>
    );
  }
  if (state.phase === 'redirect-success') {
    return <Navigate to={state.to} replace />;
  }
  // Error → route back to /login, surface reason via ?error=.
  return (
    <Navigate
      to={`${routes.login}?error=${encodeURIComponent(state.reason)}`}
      replace
    />
  );
}
